import React, { useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Alert,
  Tabs,
  Table,
  Tag,
  Progress,
  Space,
  Tooltip,
  Badge,
  Select,
  Empty,
  Timeline,
  Button,
  Checkbox,
} from 'antd';
import {
  DashboardOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  CarOutlined,
  ApiOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  LockOutlined,
  UnlockOutlined,
  LineChartOutlined,
  TableOutlined,
  SendOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  UserOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery, useLazyQuery } from '@apollo/client';
import { GET_DASHBOARD_STATS, GET_ALL_SLOTS_TELEMETRY, GET_ALL_LOCATIONS, GET_DEVICE_TELEMETRY_HISTORY, GET_DEVICE_COMMAND_HISTORY, GET_SLOT_SESSION_HISTORY, GET_SLOT_SESSION_EVENTS, GET_UNAUTHORIZED_USAGE } from '../graphql/queries';
import { Line } from '@ant-design/plots';

// Battery voltage thresholds (in mV)
const BATTERY_CRITICAL = 3300; // Below 3.3V is critical
const BATTERY_LOW = 3600; // Below 3.6V is low
const BATTERY_GOOD = 3800; // Above 3.8V is good

// Offline threshold (in minutes)
const OFFLINE_THRESHOLD_MINUTES = 5;

// Helper to format time ago
const formatTimeAgo = (date: Date | string | null): string => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Check if device is offline
const isOffline = (lastUpdated: Date | string | null): boolean => {
  if (!lastUpdated) return true;
  const now = new Date();
  const then = new Date(lastUpdated);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = diffMs / 60000;
  return diffMins > OFFLINE_THRESHOLD_MINUTES;
};

// Get battery status
const getBatteryStatus = (voltage: number | null): { color: string; status: string } => {
  if (voltage === null) return { color: 'default', status: 'Unknown' };
  if (voltage < BATTERY_CRITICAL) return { color: 'red', status: 'Critical' };
  if (voltage < BATTERY_LOW) return { color: 'orange', status: 'Low' };
  if (voltage < BATTERY_GOOD) return { color: 'lime', status: 'Good' };
  return { color: 'green', status: 'Excellent' };
};

// Dashboard Overview Tab Component
const DashboardTab: React.FC<{ stats: any; telemetry: any[]; loading: boolean }> = ({
  stats,
  telemetry,
  loading,
}) => {
  // Calculate device health metrics
  const deviceMetrics = useMemo(() => {
    if (!telemetry || telemetry.length === 0) {
      return { total: 0, withFaults: 0, lowBattery: 0, offline: 0 };
    }

    const withDeviceId = telemetry.filter((t) => t.deviceId !== null);
    const withFaults = withDeviceId.filter((t) => t.hasFault);
    const lowBattery = withDeviceId.filter(
      (t) => t.batteryVoltage !== null && t.batteryVoltage < BATTERY_LOW
    );
    const offline = withDeviceId.filter((t) => isOffline(t.lastUpdated));

    return {
      total: withDeviceId.length,
      withFaults: withFaults.length,
      lowBattery: lowBattery.length,
      offline: offline.length,
    };
  }, [telemetry]);

  // Calculate occupancy
  const occupancyPercent = useMemo(() => {
    if (!stats?.totalSlots || stats.totalSlots === 0) return 0;
    const occupied = stats.totalSlots - stats.availableSlots;
    return Math.round((occupied / stats.totalSlots) * 100);
  }, [stats]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* KPI Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Slots"
              value={stats?.totalSlots || 0}
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Available Slots"
              value={stats?.availableSlots || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Sessions"
              value={stats?.activeSessions || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Today's Revenue"
              value={stats?.todayRevenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Occupancy and Device Health */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Occupancy Rate">
            <Progress
              type="dashboard"
              percent={occupancyPercent}
              strokeColor={{
                '0%': '#52c41a',
                '50%': '#faad14',
                '100%': '#ff4d4f',
              }}
              format={(percent) => (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold' }}>{percent}%</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {(stats?.totalSlots || 0) - (stats?.availableSlots || 0)} / {stats?.totalSlots || 0}
                  </div>
                </div>
              )}
            />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Space size="large">
                <Statistic
                  title="Occupied"
                  value={(stats?.totalSlots || 0) - (stats?.availableSlots || 0)}
                  valueStyle={{ fontSize: 16 }}
                />
                <Statistic
                  title="Available"
                  value={stats?.availableSlots || 0}
                  valueStyle={{ fontSize: 16, color: '#52c41a' }}
                />
              </Space>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Device Health Summary">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Total Devices"
                  value={deviceMetrics.total}
                  prefix={<ApiOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="With Faults"
                  value={deviceMetrics.withFaults}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: deviceMetrics.withFaults > 0 ? '#ff4d4f' : '#52c41a' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Low Battery"
                  value={deviceMetrics.lowBattery}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: deviceMetrics.lowBattery > 0 ? '#faad14' : '#52c41a' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Offline"
                  value={deviceMetrics.offline}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: deviceMetrics.offline > 0 ? '#ff4d4f' : '#52c41a' }}
                />
              </Col>
            </Row>

            {/* Alert if there are issues */}
            {(deviceMetrics.withFaults > 0 || deviceMetrics.offline > 0) && (
              <Alert
                style={{ marginTop: 16 }}
                message="Attention Required"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {deviceMetrics.withFaults > 0 && (
                      <li>{deviceMetrics.withFaults} device(s) reporting faults</li>
                    )}
                    {deviceMetrics.offline > 0 && (
                      <li>{deviceMetrics.offline} device(s) offline</li>
                    )}
                    {deviceMetrics.lowBattery > 0 && (
                      <li>{deviceMetrics.lowBattery} device(s) with low battery</li>
                    )}
                  </ul>
                }
                type="warning"
                showIcon
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Revenue Summary */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Monthly Revenue"
              value={stats?.monthlyRevenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Total Parkings"
              value={stats?.totalParkings || 0}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Device Health Tab Component
const DeviceHealthTab: React.FC<{ telemetry: any[]; locations: any[]; loading: boolean }> = ({
  telemetry,
  locations,
  loading,
}) => {
  // Build location map for quick lookup
  const locationMap = useMemo(() => {
    const map: Record<string, any> = {};
    locations?.forEach((loc) => {
      map[loc.id] = loc;
    });
    return map;
  }, [locations]);

  // Build enriched telemetry data with location info
  const enrichedTelemetry = useMemo(() => {
    if (!telemetry) return [];

    return telemetry
      .filter((t) => t.deviceId !== null) // Only devices with IDs
      .map((t) => {
        const slot = locationMap[t.slotId];
        const sector = slot?.parent ? locationMap[slot.parent.id] : null;
        const parking = sector?.parent ? locationMap[sector.parent.id] : null;

        return {
          ...t,
          slotName: slot?.name || 'Unknown',
          slotCode: slot?.code || '-',
          sectorName: sector?.name || slot?.parent?.name || '-',
          parkingName: parking?.name || sector?.parent?.name || '-',
          isOffline: isOffline(t.lastUpdated),
          batteryStatus: getBatteryStatus(t.batteryVoltage),
        };
      })
      .sort((a, b) => {
        // Sort: faults first, then offline, then low battery, then by device ID
        if (a.hasFault !== b.hasFault) return a.hasFault ? -1 : 1;
        if (a.isOffline !== b.isOffline) return a.isOffline ? -1 : 1;
        if (a.batteryStatus.status !== b.batteryStatus.status) {
          const order = ['Critical', 'Low', 'Good', 'Excellent', 'Unknown'];
          return order.indexOf(a.batteryStatus.status) - order.indexOf(b.batteryStatus.status);
        }
        return (a.deviceId || 0) - (b.deviceId || 0);
      });
  }, [telemetry, locationMap]);

  // Calculate summary metrics
  const metrics = useMemo(() => {
    const total = enrichedTelemetry.length;
    const withFaults = enrichedTelemetry.filter((t) => t.hasFault).length;
    const lowBattery = enrichedTelemetry.filter(
      (t) => t.batteryVoltage !== null && t.batteryVoltage < BATTERY_LOW
    ).length;
    const offline = enrichedTelemetry.filter((t) => t.isOffline).length;
    const healthy = total - withFaults - offline;

    return { total, withFaults, lowBattery, offline, healthy };
  }, [enrichedTelemetry]);

  const columns = [
    {
      title: 'Device ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 100,
      sorter: (a: any, b: any) => (a.deviceId || 0) - (b.deviceId || 0),
      render: (id: number) => <Tag>{id}</Tag>,
    },
    {
      title: 'Slot',
      key: 'slot',
      sorter: (a: any, b: any) => (a.slotName || '').localeCompare(b.slotName || ''),
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.slotName}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.slotCode}</div>
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      sorter: (a: any, b: any) => (a.parkingName || '').localeCompare(b.parkingName || ''),
      render: (_: any, record: any) => (
        <div>
          <div>{record.parkingName}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.sectorName}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      sorter: (a: any, b: any) => {
        // Priority: OFFLINE (0) > FAULT (1) > OK (2)
        const getStatusPriority = (r: any) => {
          if (r.isOffline) return 0;
          if (r.hasFault) return 1;
          return 2;
        };
        return getStatusPriority(a) - getStatusPriority(b);
      },
      render: (_: any, record: any) => {
        if (record.isOffline) {
          return <Tag color="red">OFFLINE</Tag>;
        }
        if (record.hasFault) {
          return <Tag color="orange">FAULT</Tag>;
        }
        return <Tag color="green">OK</Tag>;
      },
    },
    {
      title: 'Barrier',
      dataIndex: 'barrierStatus',
      key: 'barrierStatus',
      width: 100,
      sorter: (a: any, b: any) => (a.barrierStatus || '').localeCompare(b.barrierStatus || ''),
      render: (status: string) => {
        const color = status === 'UP' ? 'green' : status === 'DOWN' ? 'blue' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Vehicle',
      dataIndex: 'vehicleDetected',
      key: 'vehicleDetected',
      width: 90,
      sorter: (a: any, b: any) => (a.vehicleDetected ? 1 : 0) - (b.vehicleDetected ? 1 : 0),
      render: (detected: boolean) =>
        detected ? (
          <Badge status="processing" text="Yes" />
        ) : (
          <Badge status="default" text="No" />
        ),
    },
    {
      title: 'Battery',
      key: 'battery',
      width: 120,
      sorter: (a: any, b: any) => (a.batteryVoltage || 0) - (b.batteryVoltage || 0),
      render: (_: any, record: any) => {
        if (record.batteryVoltage === null) {
          return <Tag color="default">Unknown</Tag>;
        }
        const voltage = (record.batteryVoltage / 1000).toFixed(2);
        return (
          <Tooltip title={`${record.batteryVoltage}mV`}>
            <Tag color={record.batteryStatus.color}>
              {voltage}V ({record.batteryStatus.status})
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Faults',
      key: 'faults',
      sorter: (a: any, b: any) => (a.faults?.length || 0) - (b.faults?.length || 0),
      render: (_: any, record: any) => {
        const faults = record.faults;
        if (!faults || faults.length === 0) {
          return <span style={{ color: '#52c41a' }}>None</span>;
        }
        return (
          <Space wrap>
            {faults.map((fault: string) => (
              <Tag color="red" key={fault}>
                {fault}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Last Seen',
      key: 'lastUpdated',
      width: 120,
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => {
        const dateA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
        const dateB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
        return dateA - dateB;
      },
      render: (_: any, record: any) => {
        const timeAgo = formatTimeAgo(record.lastUpdated);
        const color = record.isOffline ? '#ff4d4f' : '#52c41a';
        return (
          <Tooltip title={record.lastUpdated ? new Date(record.lastUpdated).toLocaleString() : 'Never'}>
            <span style={{ color }}>{timeAgo}</span>
          </Tooltip>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total"
              value={metrics.total}
              prefix={<ApiOutlined />}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Healthy"
              value={metrics.healthy}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ fontSize: 20, color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="With Faults"
              value={metrics.withFaults}
              prefix={<WarningOutlined />}
              valueStyle={{ fontSize: 20, color: metrics.withFaults > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Low Battery"
              value={metrics.lowBattery}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ fontSize: 20, color: metrics.lowBattery > 0 ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Offline"
              value={metrics.offline}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ fontSize: 20, color: metrics.offline > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Device Table */}
      <Card title={`All Devices (${metrics.total})`}>
        <Table
          dataSource={enrichedTelemetry}
          columns={columns}
          rowKey="slotId"
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1000 }}
          size="small"
          rowClassName={(record) => {
            if (record.hasFault) return 'device-row-fault';
            if (record.isOffline) return 'device-row-offline';
            return '';
          }}
        />
      </Card>

      {/* CSS for row highlighting */}
      <style>{`
        .device-row-fault {
          background-color: #fff2f0 !important;
        }
        .device-row-offline {
          background-color: #fffbe6 !important;
        }
        .device-row-command {
          background-color: #f9f0ff !important;
        }
        .device-row-session {
          background-color: #e6fffb !important;
        }
        .device-row-session-warning {
          background-color: #fff7e6 !important;
        }
        .device-row-event {
          background-color: #fff7e6 !important;
        }
      `}</style>
    </div>
  );
};

// Time range options for telemetry history
const TIME_RANGE_OPTIONS = [
  { value: 24, label: 'Last 24 hours' },
  { value: 48, label: 'Last 48 hours' },
  { value: 168, label: 'Last 7 days' },
  { value: 720, label: 'Last 30 days' },
];

// Battery Voltage Line Chart Component
const BatteryVoltageChart: React.FC<{ history: any[] }> = ({ history }) => {
  const chartData = useMemo(() => {
    return history
      .filter((h) => h.batteryVoltage !== null)
      .map((h) => {
        const date = new Date(h.receivedAt);
        return {
          time: `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`,
          timestamp: date.getTime(),
          voltage: h.batteryVoltage / 1000, // Convert mV to V
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [history]);

  if (chartData.length === 0) {
    return <Empty description="No battery data available" />;
  }

  const config = {
    data: chartData,
    xField: 'time',
    yField: 'voltage',
    smooth: true,
    height: 300,
    axis: {
      x: {
        title: false,
        labelAutoRotate: true,
      },
      y: {
        title: 'Voltage (V)',
        domainMin: 3.0,
        domainMax: 4.2,
      },
    },
    style: {
      stroke: '#1890ff',
      lineWidth: 2,
    },
    point: {
      shapeField: 'circle',
      sizeField: 3,
    },
    tooltip: {
      items: [
        { field: 'voltage', name: 'Battery', valueFormatter: (v: number) => `${v.toFixed(3)}V` },
      ],
    },
  };

  return <Line {...config} />;
};

// Barrier & Vehicle Status Timeline Chart
const StatusTimelineChart: React.FC<{ history: any[] }> = ({ history }) => {
  const chartData = useMemo(() => {
    const sortedHistory = [...history]
      .sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime());

    // Create data for barrier status (0 = open, 1 = closed)
    const barrierData = sortedHistory
      .filter((h) => h.lockStatus !== null)
      .map((h) => {
        const date = new Date(h.receivedAt);
        return {
          time: `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`,
          timestamp: date.getTime(),
          value: h.lockStatus === 0 ? 1 : 0, // Invert: 1 = Open (good), 0 = Closed
          type: 'Barrier Open',
        };
      });

    // Create data for vehicle detection
    const vehicleData = sortedHistory
      .filter((h) => h.vehicleDetected !== null)
      .map((h) => {
        const date = new Date(h.receivedAt);
        return {
          time: `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`,
          timestamp: date.getTime(),
          value: h.vehicleDetected ? 1 : 0,
          type: 'Vehicle Detected',
        };
      });

    return [...barrierData, ...vehicleData];
  }, [history]);

  if (chartData.length === 0) {
    return <Empty description="No status data available" />;
  }

  const config = {
    data: chartData,
    xField: 'time',
    yField: 'value',
    colorField: 'type',
    shapeField: 'hvh',
    height: 250,
    axis: {
      x: {
        title: false,
        labelAutoRotate: true,
      },
      y: {
        title: false,
        domainMin: -0.1,
        domainMax: 1.1,
        labelFormatter: (val: number) => (val >= 0.5 ? 'Yes' : 'No'),
      },
    },
    scale: {
      color: {
        range: ['#52c41a', '#722ed1'],
      },
    },
    legend: {
      color: {
        position: 'top',
      },
    },
    tooltip: {
      items: [
        { field: 'type', name: 'Status' },
        { field: 'value', name: 'Value', valueFormatter: (v: number) => (v === 1 ? 'Yes' : 'No') },
      ],
    },
    point: {
      shapeField: 'circle',
      sizeField: 3,
    },
  };

  return <Line {...config} />;
};

// Coil Frequency Line Chart Component (for debugging)
const CoilFrequencyChart: React.FC<{ history: any[] }> = ({ history }) => {
  const chartData = useMemo(() => {
    return history
      .filter((h) => h.coilFrequency !== null && h.coilFrequency !== undefined)
      .map((h) => {
        const date = new Date(h.receivedAt);
        return {
          time: `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`,
          timestamp: date.getTime(),
          frequency: h.coilFrequency,
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [history]);

  if (chartData.length === 0) {
    return <Empty description="No coil frequency data available" />;
  }

  const config = {
    data: chartData,
    xField: 'time',
    yField: 'frequency',
    smooth: true,
    height: 300,
    axis: {
      x: {
        title: false,
        labelAutoRotate: true,
      },
      y: {
        title: 'Coil Frequency',
      },
    },
    style: {
      stroke: '#722ed1',
      lineWidth: 2,
    },
    point: {
      shapeField: 'circle',
      sizeField: 3,
    },
    tooltip: {
      items: [
        { field: 'frequency', name: 'Coil Frequency', valueFormatter: (v: number) => `${v}` },
      ],
    },
  };

  return <Line {...config} />;
};

// Telemetry History Tab Component
const TelemetryHistoryTab: React.FC<{ telemetry: any[]; locations: any[] }> = ({
  telemetry,
  locations,
}) => {
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [hoursBack, setHoursBack] = useState<number>(24);

  // Build device options for selector
  const deviceOptions = useMemo(() => {
    if (!telemetry || !locations) return [];

    const locationMap: Record<string, any> = {};
    locations.forEach((loc) => {
      locationMap[loc.id] = loc;
    });

    return telemetry
      .filter((t) => t.deviceId !== null)
      .map((t) => {
        const slot = locationMap[t.slotId];
        const sector = slot?.parent ? locationMap[slot.parent.id] : null;
        const parking = sector?.parent ? locationMap[sector.parent.id] : null;

        return {
          value: t.slotId,
          label: `Device ${t.deviceId} - ${slot?.name || 'Unknown'} (${parking?.name || '-'})`,
          deviceId: t.deviceId,
          slotName: slot?.name || 'Unknown',
          parkingName: parking?.name || '-',
        };
      })
      .sort((a, b) => a.deviceId - b.deviceId);
  }, [telemetry, locations]);

  // Fetch telemetry history when device is selected
  const [fetchHistory, { loading: historyLoading, data: historyData }] = useLazyQuery(
    GET_DEVICE_TELEMETRY_HISTORY,
    { fetchPolicy: 'network-only' }
  );

  // Fetch command history when device is selected
  const [fetchCommands, { loading: commandsLoading, data: commandsData }] = useLazyQuery(
    GET_DEVICE_COMMAND_HISTORY,
    { fetchPolicy: 'network-only' }
  );

  // Fetch session history when device is selected
  const [fetchSessions, { loading: sessionsLoading, data: sessionsData }] = useLazyQuery(
    GET_SLOT_SESSION_HISTORY,
    { fetchPolicy: 'network-only' }
  );

  // Fetch session events when device is selected
  const [fetchSessionEvents, { loading: eventsLoading, data: eventsData }] = useLazyQuery(
    GET_SLOT_SESSION_EVENTS,
    { fetchPolicy: 'network-only' }
  );

  // Handle device selection
  const handleDeviceSelect = (slotId: string) => {
    setSelectedSlotId(slotId);
    const selectedDevice = deviceOptions.find((d) => d.value === slotId);
    fetchHistory({ variables: { slotId, limit: 500, hoursBack } });
    fetchSessions({ variables: { slotId, limit: 100, hoursBack } });
    fetchSessionEvents({ variables: { slotId, limit: 200, hoursBack } });
    if (selectedDevice?.deviceId) {
      fetchCommands({ variables: { deviceId: selectedDevice.deviceId, limit: 100, hoursBack } });
    }
  };

  // Handle time range change
  const handleTimeRangeChange = (hours: number) => {
    setHoursBack(hours);
    if (selectedSlotId) {
      const selectedDevice = deviceOptions.find((d) => d.value === selectedSlotId);
      fetchHistory({ variables: { slotId: selectedSlotId, limit: 500, hoursBack: hours } });
      fetchSessions({ variables: { slotId: selectedSlotId, limit: 100, hoursBack: hours } });
      fetchSessionEvents({ variables: { slotId: selectedSlotId, limit: 200, hoursBack: hours } });
      if (selectedDevice?.deviceId) {
        fetchCommands({ variables: { deviceId: selectedDevice.deviceId, limit: 100, hoursBack: hours } });
      }
    }
  };

  const history = historyData?.getDeviceTelemetryHistory || [];
  const commands = commandsData?.getDeviceCommandHistory || [];
  const sessions = sessionsData?.getSlotSessionHistory || [];
  const sessionEvents = eventsData?.getSlotSessionEvents || [];

  // Merge telemetry, commands, sessions, and session events into unified timeline
  const unifiedTimeline = useMemo(() => {
    const telemetryItems = history.map((h: any) => ({
      ...h,
      type: 'telemetry' as const,
      timestamp: new Date(h.receivedAt).getTime(),
    }));

    const commandItems = commands.map((c: any) => ({
      ...c,
      type: 'command' as const,
      timestamp: new Date(c.createdAt).getTime(),
    }));

    const sessionItems = sessions.map((s: any) => ({
      ...s,
      type: 'session' as const,
      timestamp: new Date(s.createdAt).getTime(),
    }));

    const eventItems = sessionEvents.map((e: any) => ({
      ...e,
      type: 'event' as const,
      timestamp: new Date(e.createdAt).getTime(),
    }));

    return [...telemetryItems, ...commandItems, ...sessionItems, ...eventItems].sort((a, b) => b.timestamp - a.timestamp);
  }, [history, commands, sessions, sessionEvents]);

  // Calculate battery stats from history
  const batteryStats = useMemo(() => {
    if (history.length === 0) return null;

    const voltages = history.filter((h: any) => h.batteryVoltage !== null).map((h: any) => h.batteryVoltage);
    if (voltages.length === 0) return null;

    return {
      min: Math.min(...voltages),
      max: Math.max(...voltages),
      current: voltages[0], // Most recent
      avg: Math.round(voltages.reduce((a: number, b: number) => a + b, 0) / voltages.length),
    };
  }, [history]);

  // Count events
  const eventCounts = useMemo(() => {
    if (history.length === 0) return { faults: 0, barrierChanges: 0, vehicleEvents: 0 };

    let faults = 0;
    let barrierChanges = 0;
    let vehicleEvents = 0;
    let prevLockStatus: number | null = null;
    let prevVehicle: boolean | null = null;

    history.forEach((h: any) => {
      if (h.batteryFault || h.motorFault || h.magnetFault || h.radarFault) faults++;
      if (prevLockStatus !== null && h.lockStatus !== prevLockStatus) barrierChanges++;
      if (prevVehicle !== null && h.vehicleDetected !== prevVehicle) vehicleEvents++;
      prevLockStatus = h.lockStatus;
      prevVehicle = h.vehicleDetected;
    });

    return { faults, barrierChanges, vehicleEvents };
  }, [history]);

  // Helper to get command display info
  const getCommandInfo = (commandName: string) => {
    switch (commandName) {
      case 'RAISE_BARRIER':
        return { label: 'Raise Barrier', icon: <ArrowUpOutlined />, color: 'blue' };
      case 'LOWER_BARRIER':
        return { label: 'Lower Barrier', icon: <ArrowDownOutlined />, color: 'green' };
      case 'RESET':
        return { label: 'Reset', icon: <ToolOutlined />, color: 'orange' };
      case 'GET_STATUS':
        return { label: 'Get Status', icon: <ApiOutlined />, color: 'purple' };
      default:
        return { label: commandName, icon: <SendOutlined />, color: 'default' };
    }
  };

  // Helper to get command status color
  const getCommandStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS':
      case 'ACKNOWLEDGED':
        return 'green';
      case 'FAILED':
      case 'TIMEOUT':
        return 'red';
      case 'PENDING':
      case 'SENT':
        return 'blue';
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper to get session status color
  const getSessionStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'APPROACHING':
        return 'green';
      case 'RESERVED':
        return 'blue';
      case 'DONE':
      case 'PAID':
        return 'default';
      case 'CANCELLED':
        return 'orange';
      case 'UNPAID':
        return 'red';
      default:
        return 'default';
    }
  };

  // Helper to get event trigger color and label
  const getTriggerInfo = (trigger: string) => {
    switch (trigger) {
      case 'USER':
        return { color: 'blue', label: 'User' };
      case 'STAFF':
        return { color: 'purple', label: 'Staff' };
      case 'SYSTEM':
        return { color: 'default', label: 'System' };
      case 'MQTT':
        return { color: 'green', label: 'Device' };
      case 'PAYMENT':
        return { color: 'gold', label: 'Payment' };
      default:
        return { color: 'default', label: trigger };
    }
  };

  // Unified timeline table columns
  const historyColumns = [
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: (_: any, record: any) => {
        if (record.type === 'command') {
          return <Tag color="purple" icon={<SendOutlined />}>CMD</Tag>;
        }
        if (record.type === 'session') {
          return <Tag color="cyan" icon={<PlayCircleOutlined />}>SESSION</Tag>;
        }
        if (record.type === 'event') {
          return <Tag color="orange" icon={<HistoryOutlined />}>EVENT</Tag>;
        }
        return <Tag color="geekblue" icon={<ApiOutlined />}>DATA</Tag>;
      },
    },
    {
      title: 'Time',
      key: 'time',
      width: 180,
      render: (_: any, record: any) => {
        let time;
        if (record.type === 'command' || record.type === 'session' || record.type === 'event') {
          time = record.createdAt;
        } else {
          time = record.receivedAt;
        }
        return new Date(time).toLocaleString();
      },
    },
    {
      title: 'Details',
      key: 'details',
      render: (_: any, record: any) => {
        if (record.type === 'command') {
          const cmdInfo = getCommandInfo(record.commandName);
          return (
            <Space>
              <Tag color={cmdInfo.color} icon={cmdInfo.icon}>{cmdInfo.label}</Tag>
              <Tag color={getCommandStatusColor(record.status)}>{record.status}</Tag>
              {record.sentAt && (
                <Tooltip title={`Sent: ${new Date(record.sentAt).toLocaleString()}`}>
                  <ClockCircleOutlined style={{ color: '#888' }} />
                </Tooltip>
              )}
              {record.acknowledgedAt && (
                <Tooltip title={`Ack: ${new Date(record.acknowledgedAt).toLocaleString()}`}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                </Tooltip>
              )}
            </Space>
          );
        }
        if (record.type === 'session') {
          return (
            <Space wrap size="small">
              <Tag color={getSessionStatusColor(record.status)}>{record.status}</Tag>
              <Tag color="default">{record.sessionType}</Tag>
              {record.barrierLoweredOnCreate ? (
                <Tag color="green" icon={<ArrowDownOutlined />}>Barrier Lowered</Tag>
              ) : (
                <Tag color="red" icon={<WarningOutlined />}>No Barrier Lower</Tag>
              )}
              {record.plateNumber && <Tag>{record.plateNumber}</Tag>}
              {record.createdByStaff && (
                <Tooltip title={`Created by: ${record.createdByStaff.email}`}>
                  <Tag color="purple" icon={<UserOutlined />}>
                    {record.createdByStaff.firstName || record.createdByStaff.email?.split('@')[0]}
                  </Tag>
                </Tooltip>
              )}
            </Space>
          );
        }
        if (record.type === 'event') {
          const triggerInfo = getTriggerInfo(record.trigger);
          return (
            <Space wrap size="small">
              {record.previousStatus && (
                <Tag color="default">{record.previousStatus}</Tag>
              )}
              <span>→</span>
              <Tag color={getSessionStatusColor(record.newStatus)}>{record.newStatus}</Tag>
              <Tag color={triggerInfo.color}>{triggerInfo.label}</Tag>
              {record.reason && (
                <Tooltip title="Reason">
                  <Tag color="default">{record.reason}</Tag>
                </Tooltip>
              )}
            </Space>
          );
        }
        // Telemetry data
        return (
          <Space wrap size="small">
            {record.lockStatus === 0 && <Tag color="green" icon={<UnlockOutlined />}>OPEN</Tag>}
            {record.lockStatus === 1 && <Tag color="blue" icon={<LockOutlined />}>CLOSED</Tag>}
            {record.vehicleDetected && <Badge status="processing" text="Vehicle" />}
            {record.batteryVoltage && (
              <Tooltip title={`${record.batteryVoltage}mV`}>
                <Tag color={getBatteryStatus(record.batteryVoltage).color}>
                  {(record.batteryVoltage / 1000).toFixed(2)}V
                </Tag>
              </Tooltip>
            )}
            {record.coilFrequency !== null && record.coilFrequency !== undefined && (
              <Tooltip title="Coil Frequency">
                <Tag color="purple">CF: {record.coilFrequency}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Info / Faults',
      key: 'faultsOrResponse',
      render: (_: any, record: any) => {
        if (record.type === 'command') {
          if (record.responseMessage) {
            return <span style={{ color: '#888' }}>{record.responseMessage}</span>;
          }
          if (record.responseCode !== null) {
            return <Tag color={record.responseCode === 0 ? 'green' : 'red'}>Code: {record.responseCode}</Tag>;
          }
          return <span style={{ color: '#888' }}>-</span>;
        }
        if (record.type === 'session') {
          const info: React.ReactNode[] = [];
          if (record.billingType) {
            info.push(<Tag key="billing" color="default">{record.billingType}</Tag>);
          }
          if (record.durationMinutes) {
            info.push(<span key="duration" style={{ color: '#888' }}>{record.durationMinutes}min</span>);
          }
          if (record.finalFee) {
            info.push(<Tag key="fee" color="green">${record.finalFee.toFixed(2)}</Tag>);
          }
          if (record.user) {
            info.push(
              <Tooltip key="user" title={record.user.email || record.user.phone}>
                <Tag icon={<UserOutlined />}>{record.user.firstName || 'User'}</Tag>
              </Tooltip>
            );
          } else {
            info.push(<Tag key="walkin" color="orange">Walk-in</Tag>);
          }
          return info.length > 0 ? <Space wrap size="small">{info}</Space> : <span style={{ color: '#888' }}>-</span>;
        }
        if (record.type === 'event') {
          // Show metadata or session info if available
          if (record.metadata) {
            const meta = typeof record.metadata === 'string' ? JSON.parse(record.metadata) : record.metadata;
            const metaInfo: React.ReactNode[] = [];
            if (meta.deviceId) metaInfo.push(<Tag key="device" color="default">Device: {meta.deviceId}</Tag>);
            if (meta.amount) metaInfo.push(<Tag key="amount" color="green">${meta.amount}</Tag>);
            if (meta.error) metaInfo.push(<Tag key="error" color="red">{meta.error}</Tag>);
            return metaInfo.length > 0 ? <Space wrap size="small">{metaInfo}</Space> : <span style={{ color: '#888' }}>-</span>;
          }
          return <span style={{ color: '#888' }}>-</span>;
        }
        // Telemetry faults
        const faults: string[] = [];
        if (record.batteryFault) faults.push('Battery');
        if (record.motorFault) faults.push('Motor');
        if (record.magnetFault) faults.push('Magnet');
        if (record.radarFault) faults.push('Radar');

        if (faults.length === 0) {
          return <span style={{ color: '#52c41a' }}>None</span>;
        }
        return (
          <Space wrap size="small">
            {faults.map((fault) => (
              <Tag color="red" key={fault}>{fault}</Tag>
            ))}
          </Space>
        );
      },
    },
  ];

  // Get selected device info
  const selectedDevice = deviceOptions.find((d) => d.value === selectedSlotId);

  return (
    <div>
      {/* Device and Time Range Selectors */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} lg={8}>
            <div style={{ marginBottom: 8 }}>
              <strong>Select Device:</strong>
            </div>
            <Select
              showSearch
              placeholder="Select a device to view history"
              style={{ width: '100%' }}
              value={selectedSlotId}
              onChange={handleDeviceSelect}
              options={deviceOptions}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={8} lg={5}>
            <div style={{ marginBottom: 8 }}>
              <strong>Time Range:</strong>
            </div>
            <Select
              style={{ width: '100%' }}
              value={hoursBack}
              onChange={handleTimeRangeChange}
              options={TIME_RANGE_OPTIONS}
            />
          </Col>
          <Col xs={24} sm={4} lg={3}>
            <div style={{ marginBottom: 8 }}>&nbsp;</div>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                if (selectedSlotId) {
                  const selectedDevice = deviceOptions.find((d) => d.value === selectedSlotId);
                  fetchHistory({ variables: { slotId: selectedSlotId, limit: 500, hoursBack } });
                  fetchSessions({ variables: { slotId: selectedSlotId, limit: 100, hoursBack } });
                  fetchSessionEvents({ variables: { slotId: selectedSlotId, limit: 200, hoursBack } });
                  if (selectedDevice?.deviceId) {
                    fetchCommands({ variables: { deviceId: selectedDevice.deviceId, limit: 100, hoursBack } });
                  }
                }
              }}
              disabled={!selectedSlotId}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Show empty state if no device selected */}
      {!selectedSlotId && (
        <Card>
          <Empty description="Select a device to view its telemetry history" />
        </Card>
      )}

      {/* Loading state */}
      {selectedSlotId && (historyLoading || commandsLoading || sessionsLoading || eventsLoading) && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      )}

      {/* History content */}
      {selectedSlotId && !historyLoading && !commandsLoading && !sessionsLoading && !eventsLoading && (
        <>
          {/* Stats Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={6}>
              <Card size="small">
                <Statistic
                  title="Records Found"
                  value={history.length}
                  prefix={<HistoryOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
            {batteryStats && (
              <>
                <Col xs={12} lg={4}>
                  <Card size="small">
                    <Statistic
                      title="Current Battery"
                      value={(batteryStats.current / 1000).toFixed(2)}
                      suffix="V"
                      prefix={<ThunderboltOutlined />}
                      valueStyle={{
                        fontSize: 20,
                        color: getBatteryStatus(batteryStats.current).color === 'green' ? '#52c41a' :
                               getBatteryStatus(batteryStats.current).color === 'red' ? '#ff4d4f' : '#faad14'
                      }}
                    />
                  </Card>
                </Col>
                <Col xs={12} lg={4}>
                  <Card size="small">
                    <Statistic
                      title="Min / Max"
                      value={`${(batteryStats.min / 1000).toFixed(2)} / ${(batteryStats.max / 1000).toFixed(2)}`}
                      suffix="V"
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Card>
                </Col>
              </>
            )}
            <Col xs={8} lg={3}>
              <Card size="small">
                <Statistic
                  title="Fault Events"
                  value={eventCounts.faults}
                  prefix={<WarningOutlined />}
                  valueStyle={{ fontSize: 20, color: eventCounts.faults > 0 ? '#ff4d4f' : '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={8} lg={3}>
              <Card size="small">
                <Statistic
                  title="Barrier Changes"
                  value={eventCounts.barrierChanges}
                  prefix={<LockOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={8} lg={4}>
              <Card size="small">
                <Statistic
                  title="Vehicle Events"
                  value={eventCounts.vehicleEvents}
                  prefix={<CarOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={8} lg={3}>
              <Card size="small">
                <Statistic
                  title="Commands"
                  value={commands.length}
                  prefix={<SendOutlined />}
                  valueStyle={{ fontSize: 20, color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={8} lg={3}>
              <Card size="small">
                <Statistic
                  title="Sessions"
                  value={sessions.length}
                  prefix={<PlayCircleOutlined />}
                  valueStyle={{ fontSize: 20, color: '#13c2c2' }}
                />
              </Card>
            </Col>
            <Col xs={8} lg={3}>
              <Card size="small">
                <Statistic
                  title="Events"
                  value={sessionEvents.length}
                  prefix={<HistoryOutlined />}
                  valueStyle={{ fontSize: 20, color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {unifiedTimeline.length === 0 ? (
            <Card>
              <Empty description="No telemetry or command data found for this device in the selected time range" />
            </Card>
          ) : (
            <Tabs
              defaultActiveKey="table"
              items={[
                {
                  key: 'table',
                  label: (
                    <span>
                      <TableOutlined />
                      Table View
                    </span>
                  ),
                  children: (
                    <Row gutter={16}>
                      <Col xs={24} lg={8}>
                        <Card title={`Recent Events (${unifiedTimeline.length} total)`} size="small" style={{ marginBottom: 16, height: 650, overflow: 'auto' }}>
                          <Timeline
                            key={`timeline-${hoursBack}-${unifiedTimeline.length}`}
                            items={unifiedTimeline
                              .map((item: any) => {
                                if (item.type === 'command') {
                                  const cmdInfo = getCommandInfo(item.commandName);
                                  return {
                                    color: item.status === 'SUCCESS' || item.status === 'ACKNOWLEDGED' ? 'green' :
                                           item.status === 'FAILED' || item.status === 'TIMEOUT' ? 'red' : 'blue',
                                    dot: <SendOutlined />,
                                    children: (
                                      <div>
                                        <div style={{ fontSize: 12, color: '#888' }}>
                                          {new Date(item.createdAt).toLocaleString()}
                                        </div>
                                        <div>
                                          <Space size="small">
                                            <Tag color={cmdInfo.color} icon={cmdInfo.icon}>{cmdInfo.label}</Tag>
                                            <Tag color={getCommandStatusColor(item.status)}>{item.status}</Tag>
                                          </Space>
                                        </div>
                                      </div>
                                    ),
                                  };
                                }
                                if (item.type === 'session') {
                                  return {
                                    color: !item.barrierLoweredOnCreate ? 'orange' : 'cyan',
                                    dot: <PlayCircleOutlined />,
                                    children: (
                                      <div>
                                        <div style={{ fontSize: 12, color: '#888' }}>
                                          {new Date(item.createdAt).toLocaleString()}
                                        </div>
                                        <div>
                                          <Space size="small" wrap>
                                            <Tag color={getSessionStatusColor(item.status)}>{item.status}</Tag>
                                            <Tag>{item.sessionType}</Tag>
                                            {item.barrierLoweredOnCreate ? (
                                              <Tag color="green" icon={<ArrowDownOutlined />}>Barrier ↓</Tag>
                                            ) : (
                                              <Tag color="red" icon={<WarningOutlined />}>No Barrier</Tag>
                                            )}
                                            {item.createdByStaff && (
                                              <Tag color="purple" icon={<UserOutlined />}>
                                                {item.createdByStaff.firstName || 'Staff'}
                                              </Tag>
                                            )}
                                          </Space>
                                        </div>
                                      </div>
                                    ),
                                  };
                                }
                                if (item.type === 'event') {
                                  const triggerInfo = getTriggerInfo(item.trigger);
                                  return {
                                    color: 'orange',
                                    dot: <HistoryOutlined />,
                                    children: (
                                      <div>
                                        <div style={{ fontSize: 12, color: '#888' }}>
                                          {new Date(item.createdAt).toLocaleString()}
                                        </div>
                                        <div>
                                          <Space size="small" wrap>
                                            {item.previousStatus && (
                                              <Tag color="default">{item.previousStatus}</Tag>
                                            )}
                                            <span>→</span>
                                            <Tag color={getSessionStatusColor(item.newStatus)}>{item.newStatus}</Tag>
                                            <Tag color={triggerInfo.color}>{triggerInfo.label}</Tag>
                                          </Space>
                                        </div>
                                        {item.reason && (
                                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                                            {item.reason}
                                          </div>
                                        )}
                                      </div>
                                    ),
                                  };
                                }
                                // Telemetry item
                                const faults: string[] = [];
                                if (item.batteryFault) faults.push('Battery');
                                if (item.motorFault) faults.push('Motor');
                                if (item.magnetFault) faults.push('Magnet');
                                if (item.radarFault) faults.push('Radar');
                                const hasFault = faults.length > 0;

                                return {
                                  color: hasFault ? 'red' : 'green',
                                  children: (
                                    <div>
                                      <div style={{ fontSize: 12, color: '#888' }}>
                                        {new Date(item.receivedAt).toLocaleString()}
                                      </div>
                                      <div>
                                        {hasFault ? (
                                          <Space size="small">
                                            {faults.map((f) => <Tag color="red" key={f}>{f}</Tag>)}
                                          </Space>
                                        ) : (
                                          <Space size="small">
                                            <Tag color={item.lockStatus === 0 ? 'green' : 'blue'}>
                                              {item.lockStatus === 0 ? 'Open' : 'Closed'}
                                            </Tag>
                                            {item.vehicleDetected && <Tag color="purple">Vehicle</Tag>}
                                            {item.batteryVoltage && (
                                              <Tag color={getBatteryStatus(item.batteryVoltage).color}>
                                                {(item.batteryVoltage / 1000).toFixed(2)}V
                                              </Tag>
                                            )}
                                          </Space>
                                        )}
                                      </div>
                                    </div>
                                  ),
                                };
                              })}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} lg={16}>
                        <Card
                          title={`Unified Timeline - ${selectedDevice?.slotName || 'Device'} (${history.length} telemetry, ${commands.length} commands, ${sessions.length} sessions, ${sessionEvents.length} events)`}
                          size="small"
                        >
                          <Table
                            dataSource={unifiedTimeline}
                            columns={historyColumns}
                            rowKey={(record) => record.id + (record.type === 'command' ? '-cmd' : '-tel')}
                            pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (total) => `${total} records` }}
                            scroll={{ x: 700 }}
                            size="small"
                            rowClassName={(record: any) => {
                              if (record.type === 'command') {
                                return 'device-row-command';
                              }
                              if (record.type === 'session') {
                                if (!record.barrierLoweredOnCreate) {
                                  return 'device-row-session-warning';
                                }
                                return 'device-row-session';
                              }
                              if (record.type === 'event') {
                                return 'device-row-event';
                              }
                              if (record.batteryFault || record.motorFault || record.magnetFault || record.radarFault) {
                                return 'device-row-fault';
                              }
                              return '';
                            }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: 'charts',
                  label: (
                    <span>
                      <LineChartOutlined />
                      Charts View
                    </span>
                  ),
                  children: (
                    <div>
                      {/* Battery Voltage Chart */}
                      <Card title="Battery Voltage Over Time" size="small" style={{ marginBottom: 16 }}>
                        <BatteryVoltageChart history={history} />
                      </Card>

                      {/* Coil Frequency Chart (for debugging) */}
                      <Card title="Coil Frequency Over Time (Debug)" size="small" style={{ marginBottom: 16 }}>
                        <CoilFrequencyChart history={history} />
                      </Card>

                      {/* Barrier & Vehicle Status Chart */}
                      <Card title="Barrier & Vehicle Status" size="small" style={{ marginBottom: 16 }}>
                        <StatusTimelineChart history={history} />
                      </Card>
                    </div>
                  ),
                },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
};

// Helper to format duration
const formatDuration = (minutes: number | null): string => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

// Unauthorized Usage Tab Component
const UnauthorizedTab: React.FC<{ locations: any[] }> = ({ locations }) => {
  const [selectedParkingId, setSelectedParkingId] = useState<string | undefined>(undefined);
  const [selectedSectorId, setSelectedSectorId] = useState<string | undefined>(undefined);
  const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>(undefined);
  const [hoursBack, setHoursBack] = useState<number>(168); // 7 days default
  const [includeResolved, setIncludeResolved] = useState<boolean>(true);

  // Query unauthorized usage
  const { loading, error, data, refetch } = useQuery(GET_UNAUTHORIZED_USAGE, {
    variables: {
      parkingId: selectedParkingId,
      sectorId: selectedSectorId,
      slotId: selectedSlotId,
      hoursBack,
      includeResolved,
    },
    pollInterval: 30000, // Refresh every 30 seconds
  });

  // Build location hierarchy for filters
  const parkings = useMemo(() => {
    return locations.filter((l: any) => l.type === 'PARKING');
  }, [locations]);

  const sectors = useMemo(() => {
    if (!selectedParkingId) return [];
    return locations.filter((l: any) => l.type === 'SECTOR' && l.parent?.id === selectedParkingId);
  }, [locations, selectedParkingId]);

  const slots = useMemo(() => {
    if (!selectedSectorId) return [];
    return locations.filter((l: any) => l.type === 'SLOT' && l.parent?.id === selectedSectorId);
  }, [locations, selectedSectorId]);

  // Handle parking change - reset sector and slot
  const handleParkingChange = (value: string | undefined) => {
    setSelectedParkingId(value);
    setSelectedSectorId(undefined);
    setSelectedSlotId(undefined);
  };

  // Handle sector change - reset slot
  const handleSectorChange = (value: string | undefined) => {
    setSelectedSectorId(value);
    setSelectedSlotId(undefined);
  };

  const events = data?.getUnauthorizedUsage || [];
  const activeEvents = events.filter((e: any) => e.isActive);
  const resolvedEvents = events.filter((e: any) => !e.isActive);

  // Table columns for active violations
  const activeColumns = [
    {
      title: 'Parking',
      dataIndex: 'parkingName',
      key: 'parkingName',
    },
    {
      title: 'Sector',
      dataIndex: 'sectorName',
      key: 'sectorName',
    },
    {
      title: 'Slot',
      dataIndex: 'slotCode',
      key: 'slotCode',
      render: (code: string) => (
        <Tag color="red">{code}</Tag>
      ),
    },
    {
      title: 'Device ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
    },
    {
      title: 'Since',
      dataIndex: 'detectedAt',
      key: 'detectedAt',
      render: (date: string) => formatTimeAgo(date),
    },
    {
      title: 'Duration',
      dataIndex: 'durationMinutes',
      key: 'durationMinutes',
      render: (mins: number) => (
        <Tag color="orange">{formatDuration(mins)}</Tag>
      ),
    },
  ];

  // Handle row click to filter by device
  const handleHistoryRowClick = (record: any) => {
    // Find the parking and sector for this slot
    const slot = locations.find((l: any) => l.id === record.slotId);
    if (slot) {
      const sector = slot.parent;
      const parking = sector?.parent;
      if (parking) setSelectedParkingId(parking.id);
      if (sector) setSelectedSectorId(sector.id);
      setSelectedSlotId(record.slotId);
    }
  };

  // Table columns for history
  const historyColumns = [
    {
      title: 'Parking',
      dataIndex: 'parkingName',
      key: 'parkingName',
    },
    {
      title: 'Slot',
      dataIndex: 'slotCode',
      key: 'slotCode',
      render: (code: string) => (
        <Tooltip title="Click row to filter by this device">
          <Tag color="blue" style={{ cursor: 'pointer' }}>{code}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Started',
      dataIndex: 'detectedAt',
      key: 'detectedAt',
      render: (date: string) => {
        const d = new Date(date);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      },
    },
    {
      title: 'Ended',
      dataIndex: 'resolvedAt',
      key: 'resolvedAt',
      render: (date: string | null, record: any) => {
        if (record.isActive) return <Tag color="red">Ongoing</Tag>;
        if (!date) return '-';
        const d = new Date(date);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      },
    },
    {
      title: 'Duration',
      dataIndex: 'durationMinutes',
      key: 'durationMinutes',
      render: (mins: number) => formatDuration(mins),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => (
        record.isActive
          ? <Tag color="red">Active</Tag>
          : <Tag color="green">Resolved</Tag>
      ),
    },
  ];

  if (error) {
    return (
      <Alert
        message="Error loading unauthorized usage data"
        description={error.message}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="All Parkings"
            allowClear
            style={{ width: 180 }}
            value={selectedParkingId}
            onChange={handleParkingChange}
            options={parkings.map((p: any) => ({ value: p.id, label: p.name }))}
          />
          <Select
            placeholder="All Sectors"
            allowClear
            style={{ width: 150 }}
            value={selectedSectorId}
            onChange={handleSectorChange}
            disabled={!selectedParkingId}
            options={sectors.map((s: any) => ({ value: s.id, label: s.name }))}
          />
          <Select
            placeholder="All Devices"
            allowClear
            style={{ width: 150 }}
            value={selectedSlotId}
            onChange={setSelectedSlotId}
            disabled={!selectedSectorId}
            options={slots.map((s: any) => ({ value: s.id, label: `${s.code} (${s.deviceId})` }))}
          />
          <Select
            value={hoursBack}
            onChange={setHoursBack}
            style={{ width: 140 }}
            options={TIME_RANGE_OPTIONS}
          />
          <Checkbox
            checked={includeResolved}
            onChange={(e) => setIncludeResolved(e.target.checked)}
          >
            Show resolved
          </Checkbox>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Active Violations */}
          <Card
            title={
              <Space>
                <WarningOutlined style={{ color: '#ff4d4f' }} />
                <span>Active Violations ({activeEvents.length})</span>
              </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
            styles={{ body: activeEvents.length === 0 ? {} : { padding: 0 } }}
          >
            {activeEvents.length === 0 ? (
              <Empty description="No active unauthorized vehicles" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                dataSource={activeEvents}
                columns={activeColumns}
                rowKey="id"
                pagination={false}
                size="small"
                rowClassName={() => 'unauthorized-active-row'}
              />
            )}
          </Card>

          {/* History */}
          {includeResolved && (
            <Card
              title={
                <Space>
                  <HistoryOutlined />
                  <span>History ({resolvedEvents.length})</span>
                </Space>
              }
              size="small"
              styles={{ body: resolvedEvents.length === 0 ? {} : { padding: 0 } }}
            >
              {resolvedEvents.length === 0 ? (
                <Empty description="No unauthorized events in selected period" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <Table
                  dataSource={resolvedEvents}
                  columns={historyColumns}
                  rowKey="id"
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  size="small"
                  onRow={(record) => ({
                    onClick: () => handleHistoryRowClick(record),
                    style: { cursor: 'pointer' },
                  })}
                />
              )}
            </Card>
          )}
        </>
      )}

      {/* CSS for flashing active rows */}
      <style>{`
        .unauthorized-active-row {
          background-color: #fff2f0 !important;
        }
        .unauthorized-active-row:hover > td {
          background-color: #ffccc7 !important;
        }
      `}</style>
    </div>
  );
};

// Main Analytics Page
export const Analytics: React.FC = () => {
  // Fetch dashboard stats
  const {
    loading: statsLoading,
    error: statsError,
    data: statsData,
  } = useQuery(GET_DASHBOARD_STATS, {
    pollInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch telemetry for all slots
  const {
    loading: telemetryLoading,
    error: telemetryError,
    data: telemetryData,
  } = useQuery(GET_ALL_SLOTS_TELEMETRY, {
    pollInterval: 15000, // Refresh every 15 seconds
  });

  // Fetch locations for enriching telemetry data
  const {
    loading: locationsLoading,
    data: locationsData,
  } = useQuery(GET_ALL_LOCATIONS, {
    pollInterval: 60000, // Refresh every minute
  });

  const stats = statsData?.getDashboardStats;
  const telemetry = telemetryData?.getAllSlotsTelemetry || [];
  const locations = locationsData?.getAllLocations || [];

  if (statsError || telemetryError) {
    return (
      <Alert
        message="Error loading analytics"
        description={statsError?.message || telemetryError?.message}
        type="error"
        showIcon
      />
    );
  }

  const tabItems = [
    {
      key: 'dashboard',
      label: (
        <span>
          <DashboardOutlined />
          Dashboard
        </span>
      ),
      children: (
        <DashboardTab
          stats={stats}
          telemetry={telemetry}
          loading={statsLoading || telemetryLoading}
        />
      ),
    },
    {
      key: 'devices',
      label: (
        <span>
          <ToolOutlined />
          Device Health
        </span>
      ),
      children: (
        <DeviceHealthTab
          telemetry={telemetry}
          locations={locations}
          loading={telemetryLoading || locationsLoading}
        />
      ),
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined />
          Telemetry History
        </span>
      ),
      children: (
        <TelemetryHistoryTab
          telemetry={telemetry}
          locations={locations}
        />
      ),
    },
    {
      key: 'unauthorized',
      label: (
        <span>
          <WarningOutlined style={{ color: '#ff4d4f' }} />
          Unauthorized
        </span>
      ),
      children: (
        <UnauthorizedTab
          locations={locations}
        />
      ),
    },
  ];

  return (
    <div>
      <h1>Analytics</h1>
      <Tabs defaultActiveKey="dashboard" items={tabItems} />
    </div>
  );
};
